<template>
  <div>
    <template v-for="(extraKey, extraIndex) in extrasKeysByOrder">
      <div
        :key="`extras-${extraIndex}`"
        class="cover-boost-row"
      >
        <div class="row-title">
          <v-expansion-panels
            v-model="rowPanels[extraIndex]"
            flat
          >
            <v-expansion-panel class="pa-0">
              <v-expansion-panel-header class="mt-2">
                <p class="mb-0">
                  {{ extraNames[extraKey] }}
                </p>
              </v-expansion-panel-header>
              <template v-if="extraDetailNames && extraKey in extraDetailNames">
                <div
                  v-for="(detailName, detailsIndex) in extraDetailNames[extraKey]"
                  :key="`extra-${detailName}-${detailsIndex}`"
                  class="pl-2 mb-4 font-weight-regular"
                >
                  {{ detailName }}
                </div>
              </template>
              <v-expansion-panel-content>
                <p
                  v-for="(description, descriptionIndex) in extraDescriptions[extraKey]"
                  :key="`detail-${descriptionIndex}`"
                  class="caption"
                >
                  <span v-html="description" />
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <div
          v-for="(extraValue, extraValueIndex) in extras[extraKey]"
          :key="`extra-value-${extraValueIndex}`"
          class="cell cover-boost-display divider extra"
          :class="(!isDefaqtoRatingShown && extraIndex === extrasKeysByOrder.length - 1) && 'last'"
        >
          <div
            v-if="!isDesktop"
            class="px-2"
          >
            <span class="fs-14 break">
              {{ extraNames[extraKey] }}
            </span>
          </div>
          <!-- The information icon should only be displayed on mobile and in side the first cell -->
          <qz-tooltip
            v-if="!isDesktop && extraValueIndex === 0"
            icon-float="left"
            right
          >
            <template #tooltip-body>
              <p
                v-for="(detail, detailTooltipIndex) in extraDescriptions[extraKey]"
                :key="`detail-${detailTooltipIndex}`"
                class="caption"
              >
                <span v-html="detail" />
              </p>
            </template>
          </qz-tooltip>
          <!-- The check icon needs to be realigned if the information icon is visible -->
          <v-icon
            :color="extraIconColor[extraKey][extraValueIndex]"
            class="icon mb-1"
          >
            {{ extraIcon[extraKey][extraValueIndex] }}
          </v-icon>
          <p
            class="fs-14 mb-0"
            v-html="extraCost[extraKey][extraValueIndex]"
          />
          <p
            v-if="Object.keys(extraExcess).length && extraExcess[extraKey][extraValueIndex] && !('details' in extraValue)"
            class="fs-10 mb-0 font-weight-regular"
            v-html="extraExcess[extraKey][extraValueIndex]"
          />
          <div
            v-if="'details' in extraValue && extraValue.included === 'Y'"
            class="mt-1"
          >
            <div
              v-for="(detail, detailIndex) in Object.keys(extraValue.details)"
              :key="`additional-detail-cost-${detailIndex}`"
            >
              <p
                class="fs-14 mt-2 mb-0 px-1"
                :class="isDesktop && 'd-none'"
              >
                {{ startCase(detail) }}
              </p>
              <template v-if="'included' in extraValue.details[detail] && extraValue.details[detail].included === 'Y'">
                <p
                  class="fs-14 mb-0"
                  v-html="`&pound;${convertExtraCover(extraValue.details[detail].cover)}`"
                />
                <p
                  v-if="'excess' in extraValue.details[detail] && !isExtraExcessHidden"
                  class="fs-10 mb-0 font-weight-regular sub-excess-cost"
                  v-html="`Excess: &pound;${convertExtraCover(extraValue.details[detail].excess)}`"
                />
              </template>
              <p
                v-else
                class="fs-14 my-2"
              >
                -
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  capitalize,
  startCase,
} from 'lodash';
import QzTooltip from '@/components/QzTooltip/QzTooltip';

export default {
  name: 'cover-boost-extras',
  components: {
    QzTooltip,
  },
  inject: [
    'constants',
    'helpers',
    'productName',
  ],
  props: {
    coverBoostProducts: {
      type: Array,
      required: true,
    },
    detailsDescription: {
      type: Object,
      required: true,
    },
    selectedOptionalExtras: {
      type: Array,
      required: true,
    },
    isDetailsDrawerOpen: {
      type: Boolean,
      default: false,
    },
    isDefaqtoRatingShown: {
      type: Boolean,
      default: false,
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      rowPanels: {},
    };
  },
  computed: {
    extras () {
      return this.coverBoostProducts.reduce((optionalExtras, product) => {
        Object.keys(product.optionalExtra).forEach((key) => {
          if (!optionalExtras?.[key]?.length) optionalExtras[key] = [];
          optionalExtras[key].push(product.optionalExtra[key]);
        });
        return optionalExtras;
      }, {});
    },
    extraIcon () {
      return Object.keys(this.extras).reduce((acc, extraKey) => {
        const iconsArray = this.extras[extraKey].map((productExtra) => {
          if (productExtra?.included === 'Y') {
            return 'mdi-check';
          }

          if (productExtra.included === 'extra') return 'mdi-plus';

          return 'mdi-close';
        });

        return {
          ...acc,
          [extraKey]: iconsArray,
        };
      }, {});
    },
    extraIconColor () {
      return Object.keys(this.extras).reduce((acc, extraKey) => {
        const iconsArray = this.extras[extraKey].map((productExtra) => {
          if (productExtra.cost?.value === 0 && productExtra.included === 'extra') return 'primary';

          if (this.isAdded(extraKey, productExtra, 'extra')) return 'accent';

          return productExtra?.included === 'Y' ? 'primary' : 'black';
        });

        return {
          ...acc,
          [extraKey]: iconsArray,
        };
      }, {});
    },
    extraCost () {
      return Object.keys(this.extras).reduce((acc, extraKey) => {
        const textArray = this.extras[extraKey].map((productExtra) => {
          if (productExtra.cost?.value === 0 && productExtra.included === 'extra') return 'Check with provider';

          if (this.isAdded(extraKey, productExtra, 'extra')) return `Added at &pound;${productExtra.cost.value.toFixed(2)}`;

          if (productExtra.included === 'extra') return 'Available';

          if (productExtra?.included === 'Y') {
            const coverPrice = productExtra.cover ? `&pound;${this.convertExtraCover(productExtra.cover)}` : null;
            return productExtra.cost?.text || coverPrice || 'Included';
          }
          return 'Not included';
        });

        return {
          ...acc,
          [extraKey]: textArray,
        };
      }, {});
    },
    isExtraExcessHidden () {
      // Excess on bicycle is the same for all extras, there is no need to show it
      if (['bicycle'].includes(this.productName)) return true;
      return false;
    },
    extraExcess () {
      if (this.isExtraExcessHidden) return {};

      return Object.keys(this.extras).reduce((acc, extraKey) => {
        const textArray = this.extras[extraKey].map((productExtra) => {
          if (productExtra?.included === 'Y') {
            return `Excess: &pound${this.convertExtraCover(productExtra?.excess || 0)}`;
          }
          return null;
        });

        return {
          ...acc,
          [extraKey]: textArray,
        };
      }, {});
    },
    extraNames () {
      return Object.keys(this.extras).reduce((acc, extra) => {
        const details = this.detailsDescription.content.find(item => item.slug === extra);
        return {
          ...acc,
          [extra]: capitalize(details?.name || extra.replace(/_/g, ' ')),
        };
      }, {});
    },
    extraDescriptions () {
      return Object.keys(this.extras).reduce((acc, extra) => {
        const details = this.detailsDescription.content.find(item => item.slug === extra);
        let description = ['Please check your policy details with the insurance provider for full details of this cover.'];

        if (details && 'description' in details) {
          // check if at least one price is present. If false show additional description
          const isCurrentExtraIncluded = this.extraCost[extra].some(item => item !== 'Not included');
          description = details?.isSelectableExtra && isCurrentExtraIncluded
            ? [details.description[0]]
            : details.description;
        }

        return {
          ...acc,
          [extra]: description,
        };
      }, {});
    },
    extrasKeysByOrder () {
      return this.constants.COVER_BOOST_EXTRAS_ORDER_BY_NAME || Object.keys(this.extras);
    },
    extraDetailNames () {
      return Object.keys(this.extras).reduce((acc, extraKey) => {
        // get details of first extra, because their names are identical for other products
        const productExtra = this.extras[extraKey][0];
        // don't show details names if extra is not included
        const details = productExtra?.included === 'Y' && (productExtra?.details || []);
        const detailsNames = Object.keys(details).map((detail) => details[detail]?.name || startCase(detail));

        return {
          ...acc,
          [extraKey]: detailsNames,
        };
      }, {});
    },
  },
  watch: {
    isDetailsDrawerOpen (val) {
      if (!val) {
        this.rowPanels = Array.from({ length: this.extrasKeysByOrder.length }, () => undefined);
      }
    },
  },
  methods: {
    startCase,
    isAdded (extra, value, propertyValue) {
      const key = Object.entries(extra)[0][0];
      return this.selectedOptionalExtras.includes(key) && value.included === propertyValue;
    },
    convertExtraCover (coverAmount) {
      const moreThanMax = coverAmount >= this.constants.OPTIONAL_EXTRA_COVER_MAX;

      const formattedBigCoverAmount = `${coverAmount / this.constants.OPTIONAL_EXTRA_COVER_MAX}m`;

      const formattedSmallCoverAmount = (coverAmount !== Math.floor(coverAmount)
        ? coverAmount.toFixed(2)
        : coverAmount.toLocaleString());

      return moreThanMax ? formattedBigCoverAmount : formattedSmallCoverAmount;
    },
  },
};
</script>
